import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/onix/Seo";
import { SectionEyebrow } from "../components/onix/SectionEyebrow";
import { Reveal } from "../components/onix/Reveal";
import { CTASection } from "../components/onix/CTASection";
import { CinematicScene } from "../components/onix/CinematicScene";
import { getSolutions } from "../data/solutions";
import { getTool } from "../data/tools";
import { useLang } from "../i18n/LanguageContext";

const SOL_SCENES = {
  "coordinacion-segura": "sol_coordinacion",
  "operaciones-seguridad": "sol_operaciones",
  "investigacion-incidentes": "sol_investigacion",
  "analitica-video-evidencia": "sol_video",
  "monitorizacion-exposicion": "sol_monitorizacion",
  "arquitecturas-dedicadas": "sol_arquitecturas",
};

const Row = ({ label, children }) => (
  <div className="grid md:grid-cols-12 gap-2 md:gap-8 border-t border-onix-line py-5">
    <p className="md:col-span-3 eyebrow !text-onix-gray pt-0.5">{label}</p>
    <div className="md:col-span-9 text-sm leading-relaxed text-onix-silver">{children}</div>
  </div>
);

export default function Soluciones() {
  const { lang, t } = useLang();
  const ts = t.sol;
  const tc = t.common;
  const solutions = getSolutions(lang);
  return (
    <main className="pt-[76px]">
      <Seo title={ts.seoTitle} description={ts.seoDesc} path="/soluciones" />

      <section className="onix-shell py-20 md:py-32 border-b border-onix-line">
        <Reveal>
          <SectionEyebrow index="01" label={ts.label} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-serif leading-[1.05] text-onix-white max-w-4xl mb-8">
            {ts.h1}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-onix-silver max-w-2xl">
            {ts.intro}
          </p>
        </Reveal>
      </section>

      <section className="pt-20 md:pt-28">
        {solutions.map((s) => {
          const scene = SOL_SCENES[s.id];
          return (
            <CinematicScene
              key={s.id}
              img={`/assets/img/${scene}.webp`}
              imgMobile={`/assets/img/${scene}_m.webp`}
              data-testid={`sol-scene-${s.id}`}
            >
              <div className="onix-shell py-14 md:py-20">
                <Reveal>
                  <article data-testid={`solution-${s.id}`} className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <span className="tech-label block mb-5">{s.num}</span>
                  <h2 className="font-serif font-bold text-2xl md:text-3xl leading-snug text-onix-white">{s.title}</h2>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {s.tools.map((slug) => {
                      const tool = getTool(slug, lang);
                      return (
                        <Link
                          key={slug}
                          data-testid={`solution-tool-${s.id}-${slug}`}
                          to={`/herramientas/${slug}`}
                          className="border border-onix-line px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-onix-silver hover:border-onix-silver hover:text-onix-white transition-colors"
                        >
                          {tool.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <div className="lg:col-span-8 border-b border-onix-line">
                  <Row label={ts.reto}>{s.reto}</Row>
                  <Row label={ts.enfoque}>{s.enfoque}</Row>
                  <Row label={ts.capacidades}>{s.capacidades.join(" · ")}</Row>
                  <Row label={ts.controles}>{s.controles}</Row>
                  <Row label={ts.limites}>{s.limites}</Row>
                  <Row label={ts.siguiente}>
                    <Link
                      data-testid={`solution-cta-${s.id}`}
                      to="/contacto"
                      className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.14em] text-onix-white group"
                    >
                      <span className="border-b border-onix-line pb-1 transition-colors group-hover:border-onix-silver">
                        {ts.ctaRow}
                      </span>
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true" />
                    </Link>
                  </Row>
                </div>
                  </article>
                </Reveal>
              </div>
            </CinematicScene>
          );
        })}
      </section>

      <CinematicScene img="/assets/img/onix_scene_5.webp" imgMobile="/assets/img/onix_scene_5_m.webp" data-testid="sol-scene-cta">
        <CTASection
          index="02"
          label={tc.contactoLabel}
          title={t.eco.ctaTitle}
          text={t.eco.ctaText}
          buttonLabel={tc.hablarConOnix}
          to="/contacto"
        />
      </CinematicScene>
    </main>
  );
}
